// src/pages/HelpSupport.tsx
import React, { useState } from 'react';
import { useGetIdentity } from "@refinedev/core";
import { Card, Form, Input, Select, Button, Typography, message, Space, Divider } from 'antd';
import { QuestionCircleOutlined, MailOutlined, UserOutlined, MessageOutlined } from '@ant-design/icons';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../utils/firebase';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface UserIdentity {
  id: string;
  name?: string;
  email?: string;
  plan?: string;
}

export const HelpSupport: React.FC = () => {
  const { data: identity } = useGetIdentity<UserIdentity>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Pre-populate form with user data
  React.useEffect(() => {
    if (identity) {
      form.setFieldsValue({
        name: identity.name || '',
        email: identity.email || '',
      });
    }
  }, [identity, form]);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      // Save support request
      const supportRequest = await addDoc(collection(db, 'support_requests'), {
        ...values,
        userId: identity?.id,
        status: 'new',
        createdAt: serverTimestamp(),
        userPlan: identity?.plan || 'free',
      });

      // Create email document for Firebase Extension
      await addDoc(collection(db, 'mail'), {
        to: ['support@spectreviews.com'], // Your support email
        replyTo: values.email, // User's email for easy reply
        message: {
          subject: `Support Request: ${values.subject}`,
          text: `
New support request from ${values.name}

Category: ${values.category}
User Email: ${values.email}
User ID: ${identity?.id}
Plan: ${identity?.plan || 'free'}
Support Request ID: ${supportRequest.id}

Message:
${values.message}

---
Submitted at: ${new Date().toLocaleString()}
          `,
          html: `
<h2>New Support Request</h2>
<p><strong>From:</strong> ${values.name}</p>
<p><strong>Email:</strong> ${values.email}</p>
<p><strong>Category:</strong> ${values.category}</p>
<p><strong>Subject:</strong> ${values.subject}</p>
<p><strong>User ID:</strong> ${identity?.id}</p>
<p><strong>Plan:</strong> ${identity?.plan || 'free'}</p>
<hr>
<h3>Message:</h3>
<p>${values.message.replace(/\n/g, '<br>')}</p>
<hr>
<p><small>Support Request ID: ${supportRequest.id}</small></p>
<p><small>Submitted at: ${new Date().toLocaleString()}</small></p>
          `
        }
      });

      // Also send confirmation email to user
      await addDoc(collection(db, 'mail'), {
        to: [values.email],
        message: {
          subject: 'We received your support request',
          text: `
Hi ${values.name},

Thank you for contacting Spectreviews support. We've received your request and will get back to you within 24 hours.

Your request details:
Subject: ${values.subject}
Category: ${values.category}

We'll respond to this email address: ${values.email}

Best regards,
The Spectreviews Team
          `,
          html: `
<h2>Thank you for contacting us!</h2>
<p>Hi ${values.name},</p>
<p>We've received your support request and will get back to you within 24 hours.</p>
<h3>Your request details:</h3>
<ul>
  <li><strong>Subject:</strong> ${values.subject}</li>
  <li><strong>Category:</strong> ${values.category}</li>
</ul>
<p>We'll respond to this email address: <strong>${values.email}</strong></p>
<br>
<p>Best regards,<br>The Spectreviews Team</p>
          `
        }
      });

      message.success('Support request submitted successfully! Check your email for confirmation.');
      form.resetFields(['category', 'subject', 'message']);
      
    } catch (error) {
      console.error('Error submitting support request:', error);
      message.error('Failed to submit support request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title level={2}>
              <QuestionCircleOutlined /> Help & Support
            </Title>
            <Paragraph>
              Need help? We're here for you! Submit a support request and we'll get back to you within 24 hours.
            </Paragraph>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
          >
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: 'Please enter your name' }]}
            >
              <Input 
                prefix={<UserOutlined />} 
                placeholder="Your name"
                size="large"
              />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input 
                prefix={<MailOutlined />} 
                placeholder="your@email.com"
                size="large"
              />
            </Form.Item>

            <Form.Item
              label="Category"
              name="category"
              rules={[{ required: true, message: 'Please select a category' }]}
            >
              <Select 
                placeholder="What do you need help with?"
                size="large"
              >
                <Option value="technical">Technical Issue</Option>
                <Option value="billing">Billing & Subscription</Option>
                <Option value="feature">Feature Request</Option>
                <Option value="bug">Bug Report</Option>
                <Option value="account">Account & Login</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Subject"
              name="subject"
              rules={[{ required: true, message: 'Please enter a subject' }]}
            >
              <Input 
                placeholder="Brief description of your issue"
                size="large"
              />
            </Form.Item>

            <Form.Item
              label="Message"
              name="message"
              rules={[
                { required: true, message: 'Please describe your issue' },
                { min: 20, message: 'Please provide more details (at least 20 characters)' }
              ]}
            >
              <TextArea
                placeholder="Please describe your issue in detail. Include any error messages, steps to reproduce, or specific questions you have."
                rows={6}
                showCount
                maxLength={2000}
              />
            </Form.Item>

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                size="large"
                block
              >
                Submit Support Request
              </Button>
            </Form.Item>
          </Form>

          <Divider />

          <div>
            <Title level={4}>Quick Help</Title>
            <Space direction="vertical">
              <Text>
                <strong>Common Issues:</strong>
              </Text>
              <ul>
                <li>Video generation taking too long? Check your upload status in the Videos page.</li>
                <li>Can't upload PDF? Make sure it's under 10MB and a valid PDF file.</li>
                <li>Login issues? Try resetting your password or clearing your browser cache.</li>
              </ul>
              <Text type="secondary">
                For urgent issues, you can also email us directly at support@spectreviews.com
              </Text>
            </Space>
          </div>
        </Space>
      </Card>
    </div>
  );
};